import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IHomeHeroCardsService } from "@shared/interfaces/home-hero-cards-service.interface";
import { IUploadService } from "@shared/interfaces/upload-service.interface";
import { HomeHeroCardStatus } from "@shared/types/home-hero-card/enums/home-hero-card-status.enum";
import { HomeHeroCardDto } from "@shared/types/home-hero-card/home-hero-card.dto";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useMutation } from "react-query";
import {
  extraPairsError,
  extrasToPairs,
  HomeHeroCardExtraPair,
  pairsToExtras,
} from "../home-hero-card-extras";

interface ChangeHomeHeroCardModalProps {
  open: boolean;
  card: HomeHeroCardDto | null;
  onClose: () => void;
  onSuccess: () => void;
}

const toDayjs = (value: string | null) => (value ? dayjs(value) : undefined);

const ChangeHomeHeroCardModal = ({
  open,
  card,
  onClose,
  onSuccess,
}: ChangeHomeHeroCardModalProps) => {
  const [form] = Form.useForm();
  const [imageUploaded, setImageUploaded] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const homeHeroCardsService = appInject<IHomeHeroCardsService>(
    DI_TOKENS.homeHeroCardsService,
  );
  const uploadService = appInject<IUploadService>(DI_TOKENS.uploadService);

  useEffect(() => {
    if (card) {
      form.setFieldsValue({
        ...card,
        extras: extrasToPairs(card.extras),
        activeFrom: toDayjs(card.activeFrom),
        activeTo: toDayjs(card.activeTo),
      });
      setImagePreview(card.imageUrl);
      setImageUploaded(!!card.imageUrl);
    } else {
      form.resetFields();
      form.setFieldsValue({
        status: HomeHeroCardStatus.INACTIVE,
        sortOrder: 0,
        extras: [],
      });
      setImagePreview(null);
      setImageUploaded(false);
      setImageFile(null);
    }
  }, [card, form, open]);

  const mutation = useMutation(
    (values: {
      slug: string;
      imageUrl?: string | null;
      extras?: HomeHeroCardExtraPair[];
      status: HomeHeroCardStatus;
      sortOrder?: number;
      activeFrom?: { toISOString: () => string } | null;
      activeTo?: { toISOString: () => string } | null;
    }) => {
      const extrasError = extraPairsError(values.extras);
      if (extrasError) {
        return Promise.reject(new Error(extrasError));
      }
      const payload = {
        slug: values.slug,
        imageUrl: values.imageUrl ?? null,
        extras: pairsToExtras(values.extras),
        status: values.status,
        sortOrder: values.sortOrder ?? 0,
        activeFrom: values.activeFrom?.toISOString() ?? null,
        activeTo: values.activeTo?.toISOString() ?? null,
      };
      if (card?.id) {
        return homeHeroCardsService.update({ ...payload, id: card.id });
      }
      return homeHeroCardsService.create(payload);
    },
    {
      onSuccess,
      onError: (error: Error) => {
        message.error(error?.message ?? "Failed to save home hero card");
      },
    },
  );

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      mutation.mutate(values);
    } catch {
      message.error("Please fix the form errors before submitting.");
    }
  };

  return (
    <Modal
      open={open}
      title={card ? "Edit Home Hero Card" : "Create Home Hero Card"}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={mutation.isLoading}
      okText="OK"
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="slug"
          label="Slug"
          rules={[{ required: true, message: "Please enter the slug" }]}
        >
          <Input placeholder="e.g. tournaments" disabled={!!card} />
        </Form.Item>

        <Form.Item label="Image for Card">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0] || null;
              setImageUploaded(false);
              setImageFile(file);
              setImagePreview(file ? URL.createObjectURL(file) : null);
            }}
          />
          {imagePreview && (
            <div style={{ marginTop: 10 }}>
              <img
                src={imagePreview}
                alt="Selected"
                style={{ width: "100%", maxHeight: 200, objectFit: "contain" }}
              />
              {imageUploaded === false && imageFile ? (
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    type="primary"
                    disabled={imageUploading}
                    onClick={async () => {
                      if (!imageFile) return;
                      try {
                        setImageUploading(true);
                        const {
                          data: { url },
                        } = await uploadService.uploadImage(imageFile);
                        if (url) {
                          setImageUploaded(true);
                          form.setFieldsValue({ imageUrl: url });
                        }
                      } finally {
                        setImageUploading(false);
                      }
                    }}
                    style={{ marginTop: 10 }}
                  >
                    {imageUploading ? "Uploading..." : "Use Image"}
                  </Button>
                </div>
              ) : null}
            </div>
          )}
        </Form.Item>

        <Form.Item name="imageUrl" label="Image URL (Optional)">
          <Input />
        </Form.Item>

        <Form.Item
          name="status"
          label="Status"
          rules={[{ required: true, message: "Please select status" }]}
        >
          <Select>
            <Select.Option value={HomeHeroCardStatus.ACTIVE}>
              Active
            </Select.Option>
            <Select.Option value={HomeHeroCardStatus.INACTIVE}>
              Inactive
            </Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name="sortOrder" label="Sort order">
          <InputNumber min={0} style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="activeFrom" label="Active From">
          <DatePicker showTime inputReadOnly style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item name="activeTo" label="Active To">
          <DatePicker showTime inputReadOnly style={{ width: "100%" }} />
        </Form.Item>

        <div style={{ marginBottom: 8 }}>Extras</div>
        <Form.List name="extras">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Space
                  key={key}
                  style={{ display: "flex", marginBottom: 8 }}
                  align="baseline"
                >
                  <Form.Item {...restField} name={[name, "key"]}>
                    <Input placeholder="Key" />
                  </Form.Item>
                  <Form.Item {...restField} name={[name, "value"]}>
                    <Input placeholder="Value" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} />
                </Space>
              ))}
              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                >
                  Add extra
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default ChangeHomeHeroCardModal;
